import { useEffect } from 'react'
import { Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { fetchMembershipSummary } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  calcAiUsagePercent,
  formatAiSecondsDuration,
  getPlanAiProcessingSeconds,
} from '@/features/billing/utils/membership-labels'

/**
 * 个人中心「本月 AI / MCP 处理时长」卡片。
 *
 * 展示当前套餐总时长、已用、剩余以及使用进度。
 * 额度按统一的 ai_processing_seconds 桶计算，所有 AI / MCP 工具共享。
 */
export function AiProcessingQuotaCard() {
  const session = useAuthSessionStore((state) => state.session)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)

  // 进入个人中心时拉取一次最新额度，确保剩余时长是实时的。
  useEffect(() => {
    void fetchMembershipSummary()
      .then((membership) => {
        if (membership) {
          updateMembership(membership)
        }
      })
      .catch(() => {
        // 拉取失败时保留登录态里的额度，卡片仍可正常展示。
      })
  }, [updateMembership])

  const planCode = session?.membership?.planCode ?? null
  const totalSeconds = getPlanAiProcessingSeconds(planCode)
  const remainingSeconds = session?.membership?.quotas?.aiProcessingSecondsRemaining ?? null
  const hasTotal = totalSeconds != null && totalSeconds > 0
  const usedSeconds =
    hasTotal && remainingSeconds != null
      ? Math.max(totalSeconds - remainingSeconds, 0)
      : null
  const usagePercent = calcAiUsagePercent(totalSeconds, remainingSeconds)
  const isLow = hasTotal && remainingSeconds != null && remainingSeconds <= totalSeconds * 0.1

  return (
    <section className="rounded-3xl border border-border bg-card p-6 lg:col-span-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">本月 AI / MCP 处理时长</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              所有 AI 与 MCP 自动化工具共享同一额度桶，每月按套餐重置。
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={openMembershipDialog}>
          {isLow ? '升级套餐' : '查看套餐'}
        </Button>
      </div>

      {hasTotal ? (
        <div className="mt-6 space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
              <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">
                套餐总时长
              </div>
              <div className="mt-2 text-2xl font-semibold text-foreground">
                {formatAiSecondsDuration(totalSeconds)}
              </div>
            </div>

            <div
              className={`rounded-2xl border px-4 py-4 ${
                isLow
                  ? 'border-amber-500/30 bg-amber-500/10'
                  : 'border-emerald-500/20 bg-emerald-500/10'
              }`}
            >
              <div
                className={`text-xs font-medium uppercase tracking-[0.16em] ${
                  isLow ? 'text-amber-300/80' : 'text-emerald-300/80'
                }`}
              >
                当前剩余
              </div>
              <div
                className={`mt-2 text-2xl font-semibold ${
                  isLow ? 'text-amber-100' : 'text-emerald-100'
                }`}
              >
                {remainingSeconds != null ? formatAiSecondsDuration(remainingSeconds) : '待同步'}
              </div>
            </div>

            <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
              <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">
                本月已用
              </div>
              <div className="mt-2 text-2xl font-semibold text-foreground">
                {usedSeconds != null ? formatAiSecondsDuration(usedSeconds) : '待同步'}
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>已使用 {usagePercent}%</span>
              <span>
                {usedSeconds != null ? formatAiSecondsDuration(usedSeconds) : '—'} /{' '}
                {formatAiSecondsDuration(totalSeconds)}
              </span>
            </div>
            <Progress value={usagePercent} className="h-2" />
          </div>

          {isLow ? (
            <p className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              剩余 AI 处理时长不多了，升级套餐可获得更多每月额度。
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-border/70 bg-background/40 px-4 py-6 text-center text-sm text-muted-foreground">
          暂未获取到当前套餐的 AI 处理时长配置，开通会员后这里会显示总时长与使用进度。
        </div>
      )}
    </section>
  )
}
